import { KNOWN_DIRECTIVE_NAMES } from './remarkAiDirectives';

const NAMES = KNOWN_DIRECTIVE_NAMES.join('|');

// A complete known directive on a single line: `:documentation{…}` or
// `::documentation{…}`. Same attribute-group shape as unwrapBacktickedDirectives
// — no `}` or newline inside — so an unterminated (still streaming) directive is
// never touched and is left to maskIncompleteDirective.
const DIRECTIVE_RE = new RegExp(`:{1,2}(?:${NAMES})\\{[^}\\n]*\\}`, 'g');

// A `|` not already escaped by a preceding backslash. Matched via a prefix group
// rather than a lookbehind for browser support.
const UNESCAPED_PIPE_RE = /(^|[^\\])\|/g;

// Escape `|` inside a directive's attributes so it survives a GFM table.
//
// Chip quotes are verbatim passages of the source document, and those often
// contain a `|` (e.g. text lifted from a table in a PDF report). GFM splits a
// table row into cells on `|` BEFORE any inline syntax is parsed, so inside a
// table cell `:documentation{id=x quote="a | b"}` is cut in two: the directive
// loses its attributes and degrades to literal text, and the rest of the quote
// spills into an extra cell that GFM drops — the reader sees a truncated raw
// `:documentation{id=…` fragment instead of a chip.
//
// `\|` is GFM's escape for a literal pipe in a cell, and remark-directive keeps
// it as-is in the attribute value; remarkAiDirectives turns it back into `|`.
// Escaping outside a table is equally harmless for the same reason, so this
// does not try to detect table rows. The accepted cosmetic edge, as with
// unwrapBacktickedDirectives: a directive with a `|` written inside a fenced code
// block shows the added backslash in the displayed sample.
export default function escapeDirectivePipes(text: string): string {
  if (!text.includes('|')) return text;
  return text.replace(DIRECTIVE_RE, (directive) =>
    // Run twice: adjacent pipes (`||`) share the boundary char, so a single
    // global pass only escapes every other one.
    directive
      .replace(UNESCAPED_PIPE_RE, '$1\\|')
      .replace(UNESCAPED_PIPE_RE, '$1\\|'),
  );
}
